import {
    ChangeDetectorRef, Component, OnDestroy, OnInit, ChangeDetectionStrategy
} from '@angular/core';
import { Subscription } from 'rxjs';
import { NotificationService}
    from '@app/secure/notification/notification.service';
import { Notification} from '@app/secure/model/Notification';
import { NotificationType} from '@app/secure/model/NotificationType';

@Component
({
    selector: 'app-notification',
    templateUrl: './notification.component.html',
    styleUrls: ['./notification.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
class NotificationComponent implements OnInit, OnDestroy
{
    notifications: Notification[] = [];
    private subscription: Subscription;

    /**
     * @desc   - Wire notification stream and change detector.
     * @access - public
     * @param  - notificationSvc : toast notification service
     * @param  - cdr : change detector for deferred list updates
     * @return -
     */
    constructor
    (
        private notificationSvc: NotificationService,
        private cdr: ChangeDetectorRef
    )
    {
    }

    /**
     * @desc   - Apply a toast add/clear on the next microtask so the
     *           list does not mutate mid change-detection (NG0100).
     * @access - private
     * @param  - notification : toast payload from NotificationService
     * @return -
     */
    private addNotification
    (
        notification: Notification
    )
    {
        Promise.resolve().then(() =>
        {
            this.applyNotification(notification);
        });
    }

    /**
     * @desc   - Mutate the visible toast list and refresh the view.
     * @access - private
     * @param  - notification : toast payload from NotificationService
     * @return -
     */
    private applyNotification
    (
        notification: Notification
    )
    {
        if (notification.type === NotificationType.CLEAR)
        {
            this.notifications = [];
        }
        else
        {
            this.notifications = this.notifications.concat(
                [notification]
            );

            if (notification.timeout !== 0)
            {
                setTimeout(() => this.close(notification),
                    notification.timeout);
            }
        }

        this.cdr.markForCheck();
    }

    /**
     * @desc   - Subscribe to the shared notification stream.
     * @access - public
     * @param  -
     * @return -
     */
    ngOnInit(): void
    {
        this.subscription = this.notificationSvc.getObservable().
                            subscribe(notification=>
                                      this.addNotification(notification));
    }

    /**
     * @desc   - Drop the notification subscription.
     * @access - public
     * @param  -
     * @return -
     */
    ngOnDestroy()
    {
        this.subscription.unsubscribe();
    }

    /**
     * @desc   - Remove one toast on the next microtask (NG0100-safe).
     * @access - public
     * @param  - notification : toast to close
     * @return -
     */
    close
    (
        notification
    )
    {
        Promise.resolve().then(() =>
        {
            this.notifications =
                this.notifications.filter(
                    notif => notif.id !== notification.id
                );
            this.cdr.markForCheck();
        });
    }

    /**
     * @desc   - Map notification type to CSS class name.
     * @access - public
     * @param  - notification : toast whose style is needed
     * @return - string CSS class for the toast type
     */
    className
    (
        notification: Notification
    ): string
    {
        let style: string;

        switch(notification.type)
        {
            case NotificationType.SUCCESS:
                style='success';
                break;
            case NotificationType.WARNING:
                style='warning';
                break;
            case NotificationType.ERROR:
                style='error';
                break;
            default:
                style='info';
                break;
            // end case
        }

        return style;
    }
}

export { NotificationComponent };
